import * as XLSX from 'xlsx';

/*
|--------------------------------------------------------------------------
| Export To Excel
|--------------------------------------------------------------------------
| Builds a multi-sheet .xlsx workbook from a list of sheet definitions.
| Each sheet definition is:
|   {
|     name: 'Sheet Name',           // max 31 chars, sanitized automatically
|     rows: [{ col1: val, col2: val }, ...]   // array of plain objects
|   }
|
| The first sheet is expected to be the "Summary" sheet, but any order
| works. Empty sheets (no rows) are still added with a header-only /
| placeholder row so the workbook doesn't silently drop tabs.
|--------------------------------------------------------------------------
*/

function sanitizeSheetName(name, usedNames) {
    // Excel sheet name rules: max 31 chars, no : \ / ? * [ ]
    let clean = String(name || 'Sheet')
        .replace(/[:\\/?*[\]]/g, ' ')
        .trim()
        .slice(0, 31);

    if (!clean) clean = 'Sheet';

    // Ensure uniqueness (Excel doesn't allow duplicate sheet names)
    let finalName = clean;
    let suffix = 1;
    while (usedNames.has(finalName)) {
        const base = clean.slice(0, 28);
        finalName = `${base}_${suffix}`;
        suffix++;
    }

    usedNames.add(finalName);
    return finalName;
}

/**
 * Flattens a value for safe display in an Excel cell.
 * Arrays/objects are JSON-stringified; everything else is left as-is
 * (numbers/booleans/strings/dates pass through untouched).
 */
function flattenCellValue(value) {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
        if (!value.length) return '';
        // If it's an array of primitives, join with comma
        if (value.every(v => v === null || v === undefined || typeof v !== 'object')) {
            return value.join(', ');
        }
        return JSON.stringify(value);
    }
    if (typeof value === 'object') {
        // React elements / DOM-ish objects should never reach here if callers
        // pass plain data, but guard against it just in case.
        if (value.$$typeof) return '';
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return value;
}

/**
 * Converts an array of row objects into a worksheet, flattening any
 * nested arrays/objects into readable strings.
 */
function buildWorksheet(rows) {
    if (!Array.isArray(rows) || !rows.length) {
        return XLSX.utils.aoa_to_sheet([['No data available']]);
    }

    const safeRows = rows.map(row => {
        const out = {};
        for (const [key, value] of Object.entries(row)) {
            out[key] = flattenCellValue(value);
        }
        return out;
    });

    return XLSX.utils.json_to_sheet(safeRows);
}

/**
 * Export a workbook made of multiple sheets.
 *
 * @param {Array<{name: string, rows: Array<Object>}>} sheets
 * @param {string} filename - without extension
 */
export function exportWorkbook(sheets, filename = 'export') {
    const workbook = XLSX.utils.book_new();
    const usedNames = new Set();

    for (const sheet of sheets) {
        const sheetName = sanitizeSheetName(sheet.name, usedNames);
        const worksheet = buildWorksheet(sheet.rows);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    }

    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    XLSX.writeFile(workbook, `${filename}_${stamp}.xlsx`);
}

/**
 * Converts an array of objects/arrays into row objects keyed by the
 * provided headers. Useful when existing table data is shaped as
 * arrays of arrays (e.g. AssessmentSimpleTable rows) rather than
 * objects.
 */
export function rowsFromArrays(headers, arrayRows) {
    return (arrayRows || []).map(cells => {
        const row = {};
        headers.forEach((h, i) => {
            row[h] = cells[i];
        });
        return row;
    });
}

/**
 * Builds a "Summary" sheet's rows from a flat summary object,
 * e.g. { datasets: 8, cards: 32, pages: 8, ... } -> rows of {Metric, Value}.
 */
export function summaryRowsFromObject(summary = {}, labelMap = {}) {
    return Object.entries(summary || {})
        .filter(([, value]) => typeof value !== 'object')
        .map(([key, value]) => ({
            Metric: labelMap[key] || key,
            Value: value
        }));
}
